import baseService from './support/base-service';
import * as Mutations from '../graphql/mutations';
import * as Subscriptions from '../graphql/subscriptions';
import gql from 'graphql-tag';
import awsVars from '../config/aws-exports';

// ICE servers configuration with STUN and TURN for better connectivity
// TURN servers are critical for production environments with restrictive firewalls
//
// AUTO-DETECTION:
// ===============
// This service automatically detects and uses Twilio TURN servers if available.
// If the getTwilioToken Lambda is deployed, it will be auto-discovered and used.
// No configuration needed - it just works!
//
// FALLBACK:
// If Twilio Lambda is not available, falls back to free STUN servers.
// Free STUN works on most networks but may fail on restrictive firewalls.
//
// Default ICE servers (STUN only, used as fallback)
const DEFAULT_ICE_SERVERS = [
	// Twilio's public STUN server (free, no auth)
	{ urls: 'stun:global.stun.twilio.com:3478' },
	// Google's public STUN servers (fallback)
	{ urls: 'stun:stun.l.google.com:19302' },
	{ urls: 'stun:stun1.l.google.com:19302' }
];

/**
 * Auto-detect Twilio token endpoint from AppSync GraphQL endpoint
 * AppSync and Lambda are typically on the same API Gateway
 * Lazy-loaded to avoid webpack module resolution issues
 */
function getTwilioTokenEndpoint() {
	// Check if explicitly configured (backwards compatibility)
	if (process.env.VUE_APP_twilio_token_endpoint) {
		return process.env.VUE_APP_twilio_token_endpoint;
	}
	
	// Get AWS config (lazy load)
	const AWSConfig = awsVars.default(process.env);
	
	// Try to derive from AppSync endpoint
	const appsyncEndpoint = AWSConfig && AWSConfig.aws_appsync_graphqlEndpoint;
	if (appsyncEndpoint) {
		// AppSync URL format: https://xxxxx.appsync-api.us-east-1.amazonaws.com/graphql
		// API Gateway format: https://xxxxx.execute-api.us-east-1.amazonaws.com/prod/getTwilioToken
		
		// Extract the base URL (remove /graphql path)
		const baseUrl = appsyncEndpoint.replace(/\/graphql$/, '');
		
		// Try common patterns
		// Pattern 1: Same domain, different path
		const directPath = `${baseUrl}/getTwilioToken`;
		
		// Pattern 2: Replace appsync-api with execute-api (common API Gateway pattern)
		const apiGatewayUrl = appsyncEndpoint
			.replace('appsync-api', 'execute-api')
			.replace('/graphql', '/prod/getTwilioToken');
		
		// Return the most likely endpoint (we'll try this and fall back if it fails)
		return apiGatewayUrl;
	}
	
	// If no AppSync endpoint, try relative path (works if hosted on same domain)
	return '/getTwilioToken';
}

// Cache for Twilio availability check
let twilioAvailabilityChecked = false;
let twilioAvailable = false;
let cachedTwilioEndpoint = null;

class WebRTCService extends baseService {
	constructor() {
		super();
		this.peerConnections = new Map();
		this.peerVideoTrackCounts = new Map(); // Track number of video tracks per peer to detect screen shares
		this.peerTrackedTracks = new Map(); // Track which track IDs we've already counted to avoid double-counting during renegotiation
		this.pendingOffers = new Map(); // Queue offers that arrive before local stream is ready
		this.pendingIceCandidates = new Map(); // Queue ICE candidates that arrive before remote description is set
		this.negotiationInProgress = new Map(); // Track if negotiation is in progress for each peer
		this.reconnectionCooldown = new Map(); // Track failed peers to prevent rapid reconnection (userId -> timestamp)
		this.isLocalStreamReady = false;
		this.localStream = null;
		this.screenStream = null;
		this.vaultId = null;
		this.userId = null;
		this.sessionId = null; // Unique session ID for this connection (supports multiple devices per user)
		this.isScreenSharing = false;
		this.presenceHeartbeatInterval = null;
		this.transceiverMonitorInterval = null; // Interval for monitoring transceiver states
		this.signalSubscription = null;
		this.onRemoteStreamCallback = null;
		this.onRemoteStreamRemovedCallback = null;
		this.onConnectionStateCallback = null;
		this.onScreenShareStoppedCallback = null; // Callback for when screen share is stopped
		this.twilioIceServers = null; // Cache Twilio ICE servers
		this.twilioTokenExpiry = null; // Track when Twilio token expires
	}

	async initialize(vaultId, userId, callbacks) {
		this.vaultId = vaultId;
		this.userId = userId;
		// Generate unique session ID for this connection (supports multiple devices per user)
		this.sessionId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		this.onRemoteStreamCallback = callbacks.onRemoteStream;
		this.onRemoteStreamRemovedCallback = callbacks.onRemoteStreamRemoved;
		this.onConnectionStateCallback = callbacks.onConnectionState;
		this.onScreenShareStoppedCallback = callbacks.onScreenShareStopped;

		console.log('🚀 Initializing WebRTC service...');
		console.log(`   Vault ID: ${vaultId}`);
		console.log(`   User ID: ${userId}`);
		console.log(`   Session ID: ${this.sessionId}`);

		// Log browser capabilities
		this.logBrowserCapabilities();

		// Start presence heartbeat
		await this.updatePresence('online');
		this.startPresenceHeartbeat();

		// Subscribe to WebRTC signals
		await this.subscribeToSignals();
	}

	logBrowserCapabilities() {
		console.log('🔍 Browser WebRTC Capabilities:');
		console.log(`   - getUserMedia: ${!!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)}`);
		console.log(`   - RTCPeerConnection: ${!!window.RTCPeerConnection}`);
		console.log(`   - getDisplayMedia: ${!!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)}`);
		console.log(`   - User Agent: ${navigator.userAgent}`);
		console.log(`   - Platform: ${navigator.platform}`);
	}
	
	/**
	 * Check if Twilio token endpoint is available
	 * This is called once per session to auto-detect Twilio support
	 */
	async checkTwilioAvailability() {
		twilioAvailabilityChecked = true;
		
		try {
			// Get the endpoint (lazy load)
			if (!cachedTwilioEndpoint) {
				cachedTwilioEndpoint = getTwilioTokenEndpoint();
			}
			
			console.log('🔍 Auto-detecting Twilio TURN server support...');
			console.log(`   Trying endpoint: ${cachedTwilioEndpoint}`);
			
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
			
			const response = await fetch(cachedTwilioEndpoint, {
				method: 'GET',
				signal: controller.signal
			});
			
			clearTimeout(timeoutId);
			
			if (response.ok) {
				const data = await response.json();
				if (data.iceServers && Array.isArray(data.iceServers)) {
					twilioAvailable = true;
					console.log('✅ Twilio TURN servers detected and enabled');
					return;
				}
			}
			
			console.log('📡 Twilio not available, using free STUN servers');
			twilioAvailable = false;
			
		} catch (error) {
			// Silently fall back to STUN - this is expected if Lambda isn't deployed
			console.log('📡 Twilio not available, using free STUN servers');
			twilioAvailable = false;
		}
	}

	/**
	 * Get ICE servers configuration (STUN/TURN)
	 * Auto-detects Twilio availability and falls back to free STUN servers
	 */
	async getIceServers() {
		// If we haven't checked Twilio availability yet, try it once
		if (!twilioAvailabilityChecked) {
			await this.checkTwilioAvailability();
		}

		// If Twilio is not available, use default STUN servers
		if (!twilioAvailable) {
			return DEFAULT_ICE_SERVERS;
		}

		// Check if we have a valid cached token
		if (this.twilioIceServers && this.twilioTokenExpiry && Date.now() < this.twilioTokenExpiry) {
			console.log('📡 Using cached Twilio ICE servers');
			return this.twilioIceServers;
		}

		// Fetch new Twilio token
		try {
			console.log('📡 Fetching dynamic Twilio ICE servers...');
			
			// Get the endpoint (should be cached by now)
			if (!cachedTwilioEndpoint) {
				cachedTwilioEndpoint = getTwilioTokenEndpoint();
			}
			
			const response = await fetch(cachedTwilioEndpoint);
			
			if (!response.ok) {
				throw new Error(`Twilio token endpoint returned ${response.status}`);
			}

			const data = await response.json();
			
			if (!data.iceServers || !Array.isArray(data.iceServers)) {
				throw new Error('Invalid Twilio token response format');
			}

			// Cache the token
			this.twilioIceServers = data.iceServers;
			// Set expiry to 1 hour before actual expiry for safety margin
			this.twilioTokenExpiry = data.expiresAt 
				? new Date(data.expiresAt).getTime() - (60 * 60 * 1000)
				: Date.now() + (23 * 60 * 60 * 1000); // 23 hours default

			console.log('✅ Successfully fetched Twilio ICE servers (TURN enabled)');
			return this.twilioIceServers;

		} catch (error) {
			console.error('❌ Failed to fetch Twilio ICE servers, falling back to STUN only:', error.message);
			
			// Mark Twilio as unavailable so we don't keep trying
			twilioAvailable = false;
			
			// Fall back to free STUN servers
			return DEFAULT_ICE_SERVERS;
		}
	}

	async updatePresence(status) {
		// Guard: Don't attempt to update presence if vaultId or sessionId is not set
		if (!this.vaultId || !this.sessionId) {
			console.warn('⚠️ Cannot update presence: vaultId or sessionId not set');
			return;
		}
		
		return this.updatePresenceWithIds(this.vaultId, this.sessionId, status);
	}
	
	async updatePresenceWithIds(vaultId, sessionId, status) {
		try {
			console.log(`📡 Updating presence to "${status}" for vault:`, vaultId, 'session:', sessionId);
			const result = await this.mutation('updateVaultPresence', {
				vault_id: vaultId,
				session_id: sessionId,
				status: status
			});
			console.log('✅ Presence updated successfully:', result);
		} catch (error) {
			console.error('❌ Error updating presence:', error);
			console.error('   Vault ID:', this.vaultId);
			console.error('   Session ID:', this.sessionId);
			console.error('   Status:', status);
			console.error('   This could mean the vaultPresence Lambda is not deployed or has errors');
		}
	}

	startPresenceHeartbeat() {
		// Send heartbeat every 30 seconds
		this.presenceHeartbeatInterval = setInterval(() => {
			this.updatePresence('online');
		}, 30000);
		
		// Also start transceiver monitoring
		this.startTransceiverMonitoring();
	}

	stopPresenceHeartbeat() {
		if (this.presenceHeartbeatInterval) {
			clearInterval(this.presenceHeartbeatInterval);
			this.presenceHeartbeatInterval = null;
		}
		
		// Also stop transceiver monitoring
		this.stopTransceiverMonitoring();
	}
	
	startTransceiverMonitoring() {
		// Monitor transceivers every 2 seconds to detect and clean up stale screen shares
		this.transceiverMonitorInterval = setInterval(() => {
			this.checkForStaleScreenShares();
		}, 2000);
		console.log('📡 Started transceiver monitoring (checking every 2 seconds)');
	}
	
	stopTransceiverMonitoring() {
		if (this.transceiverMonitorInterval) {
			clearInterval(this.transceiverMonitorInterval);
			this.transceiverMonitorInterval = null;
			console.log('📡 Stopped transceiver monitoring');
		}
	}
	
	checkForStaleScreenShares() {
		// Iterate through all peer connections and check for inactive screen transceivers
		this.peerConnections.forEach((pc, sessionId) => {
			// Skip check if currently negotiating (tracks may be temporarily inactive)
			if (this.negotiationInProgress.get(sessionId)) {
				return;
			}
			
			// Skip check if not in stable state (might be mid-negotiation)
			if (pc.signalingState !== 'stable') {
				return;
			}
			
			const transceivers = pc.getTransceivers();
			let videoTransceiverIndex = 0;
			let activeVideoCount = 0;
			
			// Count active video transceivers
			transceivers.forEach(t => {
				if (t.receiver && t.receiver.track && t.receiver.track.kind === 'video') {
					const isActive = t.currentDirection && t.currentDirection !== 'inactive' && t.receiver.track.readyState === 'live';
					if (isActive) {
						activeVideoCount++;
					}
				}
			});
			
			// Check if our tracked count differs from actual active count
			const trackedCount = this.peerVideoTrackCounts.get(sessionId) || 0;
			
			// Only trigger if we lost SOME tracks (screen share ended) not ALL tracks (that would be a connection issue)
			// Require at least 1 active track remaining to avoid false positives during connection issues
			if (trackedCount > activeVideoCount && trackedCount > 1 && activeVideoCount > 0) {
				console.log(`🧹 [Monitor] Detected stale screen share for peer ${sessionId}: tracked=${trackedCount}, active=${activeVideoCount}`);
				this.peerVideoTrackCounts.set(sessionId, activeVideoCount);
				
				// Notify UI to clear screen stream
				if (this.onRemoteStreamCallback) {
					console.log(`   📢 [Monitor] Notifying UI to clear stale screen stream`);
					this.onRemoteStreamCallback(sessionId, null, true);
				}
			}
			
			// Also check for explicitly inactive screen transceivers
			videoTransceiverIndex = 0;
			transceivers.forEach((t, idx) => {
				if (t.receiver && t.receiver.track && t.receiver.track.kind === 'video') {
					const isScreenTransceiver = videoTransceiverIndex >= 1;
					videoTransceiverIndex++;
					
					if (isScreenTransceiver && t.currentDirection === 'inactive') {
						const count = this.peerVideoTrackCounts.get(sessionId) || 0;
						// Only log and act if we haven't already handled this
						if (count > 1) {
							console.log(`🧹 [Monitor] Found inactive screen transceiver for peer ${sessionId} at index ${idx}`);
							this.peerVideoTrackCounts.set(sessionId, count - 1);
							console.log(`   📊 [Monitor] Decremented video track count to: ${count - 1}`);
							
							if (this.onRemoteStreamCallback) {
								console.log(`   📢 [Monitor] Notifying UI to clear inactive screen stream`);
								this.onRemoteStreamCallback(sessionId, null, true);
							}
						}
						// Skip logging on subsequent checks when count is already decremented
					}
				}
			});
		});
	}

	async subscribeToSignals() {
		const self = this;
		
		console.log(`🔔 Subscribing to WebRTC signals for vault: ${this.vaultId}, user: ${this.userId}`);
		
		// Explicitly log what we expect to match on the server side
		console.log(`🔍 Expected AppSync variables: vault_id=${this.vaultId}, to_user_id=${this.userId}`);

		try {
			const subscription = await this.appSyncClient.subscribe({
				query: gql(Subscriptions.onWebRTCSignal),
				variables: {
					vault_id: this.vaultId,
					to_user_id: this.userId
				},
				fetchPolicy: 'network-only' // Changed from no-cache to network-only
			});

		this.signalSubscription = subscription.subscribe({
			next: (data) => {
				console.log('🔔 RAW Subscription data received:', JSON.stringify(data, null, 2));
				// Check both possible structures: data.data or direct data property depending on client version
				const signalData = data.data ? data.data.onWebRTCSignal : data.onWebRTCSignal;
				
				if (signalData) {
					console.log('📥 Parsed signal:', signalData);
					self.handleIncomingSignal(signalData).catch(err => console.error('Error handling signal:', err));
				} else if (data && data.value && data.value.data && data.value.data.onWebRTCSignal) {
					// Sometimes AppSync client wraps data in a value property
					const deepSignal = data.value.data.onWebRTCSignal;
					console.log('📥 Parsed signal (deep):', deepSignal);
					self.handleIncomingSignal(deepSignal).catch(err => console.error('Error handling signal:', err));
				} else {
					console.warn('⚠️ Received subscription data but no signal found in known paths. Data keys:', Object.keys(data || {}));
					console.warn('Data dump:', JSON.stringify(data));
				}
			},
			error: (error) => {
				console.error('❌ Signal subscription error:', error);
				console.error('Error details:', JSON.stringify(error, null, 2));
			}
		});
			
			console.log('✅ Successfully subscribed to WebRTC signals');
		} catch (error) {
			console.error('❌ Error subscribing to signals:', error);
		}
	}

	async handleIncomingSignal(signal) {
		const { id, from_user_id, from_user_name, signal_type, signal_data } = signal;

		console.log(`📥 Received signal notification: ${signal_type} from ${from_user_id}`);

		let resolvedSignalType = signal_type;
		let resolvedSignalData = null;
		let fromSessionId = null;

		// CAPTURE THE SIGNAL ID IMMEDIATELY - don't let subsequent subscriptions overwrite it
		const currentSignalId = id || from_user_name || null;
		
		if (signal_data && typeof signal_data === 'string') {
			const trimmed = signal_data.trim();
			if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
				try {
					resolvedSignalData = JSON.parse(trimmed);
					fromSessionId = resolvedSignalData.fromSessionId;
					console.log('✅ Parsed signal payload directly from subscription');
				} catch (parseError) {
					console.warn('⚠️ Failed to parse inline signal payload, will fetch via ID:', parseError.message);
				}
			}
		}
		
		if (!resolvedSignalData) {
			if (!currentSignalId) {
				console.error('❌ No signal ID or payload received (checked id, from_user_name, signal_data)');
				return;
			}

			console.log(`🔍 Fetching signal data for ID: ${currentSignalId}`);

			try {
				// Add retry logic with delays to handle DynamoDB eventual consistency
				let result = null;
				let retries = 0;
				const maxRetries = 8;
				const retryDelay = 800;

				while (retries < maxRetries && !result) {
					await new Promise(resolve => setTimeout(resolve, retryDelay));

					try {
						console.log(`🔍 Attempting to fetch signal data for ID: ${currentSignalId} (attempt ${retries + 1}/${maxRetries})`);
						result = await this.query('getWebRTCSignal', {
							signal_id: currentSignalId
						});

						// Debug the response structure
						console.log(`🔍 Query response structure:`, {
							hasResult: !!result,
							hasData: !!(result && result.data),
							hasGetWebRTCSignal: !!(result && result.data && result.data.getWebRTCSignal),
							resultKeys: result ? Object.keys(result) : 'no result',
							dataKeys: (result && result.data) ? Object.keys(result.data) : 'no data'
						});

						if (result && result.data && result.data.getWebRTCSignal) {
							console.log(`✅ Retrieved full signal data on attempt ${retries + 1}: ${result.data.getWebRTCSignal.signal_type}`);
							break;
						} else if (result && result.id && result.signal_type) {
							// Handle case where signal data is returned directly
							console.log(`✅ Retrieved signal data directly on attempt ${retries + 1}: ${result.signal_type}`);
							// Wrap it in the expected structure
							result = { data: { getWebRTCSignal: result } };
							break;
						} else {
							console.warn(`⚠️ Signal not found on attempt ${retries + 1}:`, currentSignalId, result);
							result = null;
							retries++;
						}
					} catch (queryError) {
						console.error(`❌ Query error on attempt ${retries + 1}:`, queryError);
						retries++;
					}
				}

				if (!result || !result.data || !result.data.getWebRTCSignal) {
					console.error('❌ Signal not found after all retries:', currentSignalId);
					console.error('Last result:', result);
					return;
				}

				const fullSignal = result.data.getWebRTCSignal;
				resolvedSignalType = fullSignal.signal_type || resolvedSignalType;
				resolvedSignalData = JSON.parse(fullSignal.signal_data);
				fromSessionId = resolvedSignalData.fromSessionId;
				console.log(`✅ Retrieved full signal data, type: ${resolvedSignalType}`);
			} catch (error) {
				console.error('Error fetching signal payload:', error);
				return;
			}
		}
		
		// Ignore signals from our own other sessions (same user, different device)
		if (fromSessionId && fromSessionId === this.sessionId) {
			console.log(`🔇 Ignoring signal from own session: ${fromSessionId}`);
			return;
		}
		
		// Use session ID as peer ID if available, otherwise fall back to user ID
		const peerId = fromSessionId || from_user_id;
		
		try {
			switch (resolvedSignalType) {
				case 'offer':
					console.log('📨 Processing offer from', from_user_id, 'session:', fromSessionId);
					await this.handleOffer(peerId, resolvedSignalData);
					break;
				case 'answer':
					console.log('📨 Processing answer from', from_user_id, 'session:', fromSessionId);
					await this.handleAnswer(peerId, resolvedSignalData);
					break;
				case 'ice-candidate':
					console.log('🧊 Processing ICE candidate from', from_user_id, 'session:', fromSessionId);
					await this.handleIceCandidate(peerId, resolvedSignalData);
					break;
				case 'leave':
					console.log('👋 Peer leaving:', from_user_id, 'session:', fromSessionId);
					this.handlePeerLeave(peerId);
					break;
				default:
					console.warn('⚠️ Unknown signal type received:', resolvedSignalType);
			}
		} catch (error) {
			console.error('Error handling signal:', error);
		}
	}

	async sendSignal(toUserId, signalType, signalData) {
		return this.sendSignalWithIds(this.vaultId, this.sessionId, this.userId, toUserId, signalType, signalData);
	}
	
	async sendSignalWithIds(vaultId, sessionId, fromUserId, toUserId, signalType, signalData) {
		try {
			console.log(`📤 Sending ${signalType} to ${toUserId}`);
			
			// Include session information in the signal data
			const enrichedData = {
				...signalData,
				fromSessionId: sessionId,
				fromUserId: fromUserId
			};
			
			const payload = JSON.stringify(enrichedData);
			console.log(`📦 Signal payload size: ${payload.length} bytes`);
			
			// Debug: Log what mutation variables we are sending
			console.log(`🔍 Sending mutation with: vault_id=${vaultId}, to_user_id=${toUserId}, signal_type=${signalType}, from_session=${sessionId}`);
			
			await this.mutation('sendWebRTCSignal', {
				vault_id: vaultId,
				to_user_id: toUserId,
				signal_type: signalType,
				signal_data: payload
			});
			console.log(`✅ Successfully sent ${signalType} to ${toUserId}`);
		} catch (error) {
			console.error(`❌ Error sending ${signalType} to ${toUserId}:`, error);
		}
	}

	async createPeerConnection(peerId, userId) {
		// Auto-detect and get ICE servers (Twilio TURN if available, otherwise free STUN)
		const iceServers = await this.getIceServers();
		
		console.log(`🔧 Creating peer connection with ${iceServers.length} ICE server(s)`);
		
		const pc = new RTCPeerConnection({
			iceServers: iceServers,
			iceCandidatePoolSize: 10,
			bundlePolicy: 'max-bundle',
			rtcpMuxPolicy: 'require'
		});
		
		// Store userId with the peer connection for signaling
		pc._userId = userId;
		
		// Add local stream tracks
		if (this.localStream) {
		// IMPORTANT: Add tracks in consistent order (audio first, then video) to prevent SDP m-line ordering issues
		const audioTracks = this.localStream.getAudioTracks();
		const videoTracks = this.localStream.getVideoTracks();
		const tracks = [...audioTracks, ...videoTracks]; // Consistent order: audio then video
		
		console.log(`📊 Local stream for peer ${peerId}:`, {
			streamId: this.localStream.id,
			streamActive: this.localStream.active,
			trackCount: tracks.length,
			tracks: tracks.map(t => ({
				kind: t.kind,
				id: t.id,
				label: t.label,
				enabled: t.enabled,
				readyState: t.readyState,
				muted: t.muted
			}))
		});
		
		tracks.forEach(track => {
			if (track.readyState === 'live') {
				console.log(`✅ Adding ${track.kind} track to peer ${peerId} (${track.id})`);
				pc.addTrack(track, this.localStream);
			} else {
				console.error(`❌ Track ${track.kind} is not live (${track.readyState}) - not adding to peer ${peerId}`);
			}
		});
		} else {
			console.error('❌ CRITICAL: No local stream available when creating peer connection for', peerId);
			console.error('   This should not happen - offers should be queued until local stream is ready!');
			console.error('   isLocalStreamReady:', this.isLocalStreamReady);
		}

		// Handle ICE candidates
		pc.onicecandidate = (event) => {
			if (event.candidate) {
				console.log(`🧊 Generated ICE candidate for peer ${peerId}, type: ${event.candidate.type}`);
				// Send to userId (not sessionId) so all their sessions receive it
				this.sendSignal(userId, 'ice-candidate', event.candidate.toJSON());
			} else {
				console.log(`✅ ICE gathering complete for peer ${peerId}`);
			}
		};

		// Handle remote stream (can receive multiple video tracks: camera + screen)
		pc.ontrack = (event) => {
			console.log(`🎥 Received ${event.track.kind} track from peer ${peerId}`);
			console.log(`   Track ID: ${event.track.id}`);
			console.log(`   Track label: "${event.track.label}"`);
			console.log(`   Track readyState: ${event.track.readyState}`);
			console.log(`   Event streams:`, event.streams ? event.streams.map(s => s.id) : 'none');
			console.log(`   Event transceiver:`, event.transceiver ? event.transceiver.direction : 'none');
			
			if (this.onRemoteStreamCallback) {
				// Create a new MediaStream containing only this track
				// This ensures camera and screen tracks are in separate streams
				const stream = new MediaStream([event.track]);
				console.log('✅ Created MediaStream for track:', {
					streamId: stream.id,
					streamActive: stream.active,
					trackInStream: stream.getTracks().length,
					trackDetails: stream.getTracks().map(t => ({
						kind: t.kind,
						id: t.id,
						enabled: t.enabled,
						readyState: t.readyState,
						muted: t.muted
					}))
				});
				
			// Determine if this is a screen share track
			// Strategy: Track count per peer - first video track is camera, subsequent video tracks are screen
			// IMPORTANT: Check if we've already counted this track to avoid double-counting during renegotiation
			let isScreenTrack = false;
			
			if (event.track.kind === 'video') {
				// Get or create tracked tracks set for this peer
				if (!this.peerTrackedTracks.has(peerId)) {
					this.peerTrackedTracks.set(peerId, new Set());
				}
				const trackedTracks = this.peerTrackedTracks.get(peerId);
				
				// Only increment count if this is a new track
				if (!trackedTracks.has(event.track.id)) {
					trackedTracks.add(event.track.id);
					const currentCount = this.peerVideoTrackCounts.get(peerId) || 0;
					this.peerVideoTrackCounts.set(peerId, currentCount + 1);
					console.log(`   📊 Video track count for peer ${peerId}: ${currentCount + 1} (new track ${event.track.id})`);
				} else {
					console.log(`   ℹ️ Track ${event.track.id} already counted for peer ${peerId} (renegotiation), skipping increment`);
				}
				
				const currentCount = this.peerVideoTrackCounts.get(peerId) || 0;
				
				// First video track is camera, second+ video tracks are screen
				// Also check label as a fallback
				const label = event.track.label.toLowerCase();
				const labelIndicatesScreen = label.includes('screen') ||
					label.includes('window') ||
					label.includes('display') ||
					label.includes('monitor') ||
					label.includes('web-contents') ||
					label.includes('capture');
				
				isScreenTrack = (currentCount >= 2) || labelIndicatesScreen;
				
				console.log(`   🔍 Screen detection: trackCount=${currentCount}, labelMatch=${labelIndicatesScreen}, result=${isScreenTrack ? 'SCREEN' : 'CAMERA'}`);
			}
				
				console.log(`   ⭐ Track type determined: ${isScreenTrack ? '🖥️ SCREEN' : '📹 CAMERA'}`);
				
				// Handle track ended (when remote user stops screen sharing or disconnects camera)
				event.track.onended = () => {
					console.log(`🛑 Track onended event fired for peer ${peerId}, track: ${event.track.id}, type: ${isScreenTrack ? 'SCREEN' : 'CAMERA'}`);
					console.log(`   Track kind: ${event.track.kind}, readyState: ${event.track.readyState}`);
					
				// Decrement video track count for this peer and remove from tracked tracks
				if (event.track.kind === 'video') {
					// Remove from tracked tracks
					const trackedTracks = this.peerTrackedTracks.get(peerId);
					if (trackedTracks) {
						trackedTracks.delete(event.track.id);
					}
					
					const count = this.peerVideoTrackCounts.get(peerId) || 0;
					if (count > 0) {
						this.peerVideoTrackCounts.set(peerId, count - 1);
						console.log(`   📊 Video track count for peer ${peerId} decreased to: ${count - 1}`);
					}
				}
					
					// If screen track ended, notify to switch back to camera
					if (isScreenTrack && this.onRemoteStreamRemovedCallback) {
						// Don't remove the entire participant, just clear their screen stream
						console.log(`   📢 Notifying that screen track ended (participant camera should remain)`);
						this.onRemoteStreamCallback(peerId, null, true); // null stream with isScreenTrack = true means "remove screen"
					}
				};
				
				// Also monitor for muted tracks (when track stops receiving data but doesn't end)
				if (event.track.kind === 'video' && isScreenTrack) {
					console.log(`   📹 Setting up data flow monitoring for screen track ${event.track.id}`);
					
					// Check track muted state changes
					event.track.onmute = () => {
						console.log(`🔇 Screen track muted for peer ${peerId}, track: ${event.track.id}`);
					};
					
					event.track.onunmute = () => {
						console.log(`🔊 Screen track unmuted for peer ${peerId}, track: ${event.track.id}`);
					};
				}
				
				// Pass stream with metadata about track type
				this.onRemoteStreamCallback(peerId, stream, isScreenTrack);
			} else {
				console.warn('⚠️ ontrack event but no callback registered');
			}
		};

		// Handle connection state changes
		pc.onconnectionstatechange = () => {
			console.log(`🔗 Peer ${peerId} connection state: ${pc.connectionState}`);
			if (this.onConnectionStateCallback) {
				this.onConnectionStateCallback(peerId, pc.connectionState);
			}
			
			if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
				console.error(`❌ Connection ${pc.connectionState} for peer ${peerId}`);
				
				// Add reconnection cooldown to prevent rapid reconnection attempts
				// Extract userId from peerId (might be sessionId format: userId_timestamp_random)
				const peerUserId = peerId.includes('_') ? peerId.split('_')[0] : peerId;
				this.reconnectionCooldown.set(peerUserId, Date.now());
				console.log(`   ⏱️ Set reconnection cooldown for ${peerUserId} (5 second delay)`);
				
				this.handlePeerLeave(peerId);
			} else if (pc.connectionState === 'connected') {
				console.log(`✅ Successfully connected to peer ${peerId}`);
				
				// Clear reconnection cooldown on successful connection
				const peerUserId = peerId.includes('_') ? peerId.split('_')[0] : peerId;
				this.reconnectionCooldown.delete(peerUserId);
			}
		};

		// Handle ICE connection state changes for better debugging
		pc.oniceconnectionstatechange = () => {
			console.log(`🧊 Peer ${peerId} ICE connection state: ${pc.iceConnectionState}`);
			if (pc.iceConnectionState === 'failed') {
				console.error(`❌ ICE connection failed for peer ${peerId}. May need TURN servers.`);
			}
		};

		// Handle ICE gathering state
		pc.onicegatheringstatechange = () => {
			console.log(`🧊 Peer ${peerId} ICE gathering state: ${pc.iceGatheringState}`);
		};

		// Log signaling state changes
		pc.onsignalingstatechange = () => {
			console.log(`📡 Peer ${peerId} signaling state: ${pc.signalingState}`);
		};

	// Handle renegotiation (when peer adds/removes tracks after connection established)
	// NOTE: This fires when WE add/remove tracks locally. When the remote peer adds tracks,
	// they send us an offer and we respond with an answer - we don't need to create our own offer.
	pc.onnegotiationneeded = async () => {
		console.log(`🔄 Negotiation needed for peer ${peerId}, signaling state: ${pc.signalingState}`);
		
		// Check and set the lock ATOMICALLY to prevent race conditions
		if (this.negotiationInProgress.get(peerId)) {
			console.log(`   ⏸️ Skipping negotiation - already in progress for peer ${peerId}`);
			return;
		}
		
		// Set lock immediately before any async operations
		this.negotiationInProgress.set(peerId, true);
		
		try {
			// Only create offer if:
			// 1. We're in stable state (not currently negotiating)
			// 2. We have local tracks that we're sending (otherwise this might be triggered by receiving remote tracks)
			if (pc.signalingState !== 'stable') {
				console.log(`   ⏸️ Skipping negotiation - not in stable state (${pc.signalingState})`);
				this.negotiationInProgress.set(peerId, false);
				return;
			}
			
			// Check if we have active local senders
			const senders = pc.getSenders();
			const activeSenders = senders.filter(s => s.track && s.track.readyState === 'live');
			console.log(`   📊 Active local senders: ${activeSenders.length}/${senders.length}`);
			
		// Only renegotiate if we have local tracks to send
		// This prevents unnecessary renegotiation when we're just receiving remote tracks
		if (activeSenders.length > 0) {
			console.log(`   ✅ Creating new offer for peer ${peerId} (we have local tracks to send)`);
			
			try {
				const offer = await pc.createOffer();
				
				// Double-check state before setting local description
				// If state changed, the remote peer may have sent an offer (glare condition)
				if (pc.signalingState !== 'stable') {
					console.warn(`   ⚠️ Signaling state changed to ${pc.signalingState} after createOffer - aborting local offer`);
					this.negotiationInProgress.set(peerId, false);
					return;
				}
				
				await pc.setLocalDescription(offer);
				await this.sendSignal(userId, 'offer', { type: offer.type, sdp: offer.sdp });
				
				// Clear negotiation flag after a delay to allow the remote peer to process
				// Use a timeout to prevent rapid successive negotiations
				setTimeout(() => {
					this.negotiationInProgress.set(peerId, false);
				}, 1000);
			} catch (offerError) {
				console.error(`   ❌ Error creating/setting offer for ${peerId}:`, offerError.message);
				
				// If we get an m-line order error, try to rollback and let the remote peer initiate
				if (offerError.message && offerError.message.includes('m-line')) {
					console.log(`   🔄 Attempting rollback due to m-line order error`);
					try {
						await pc.setLocalDescription({ type: 'rollback' });
						console.log(`   ✅ Rollback successful - will wait for remote offer`);
					} catch (rollbackError) {
						console.error(`   ❌ Rollback failed:`, rollbackError.message);
					}
				}
				
				this.negotiationInProgress.set(peerId, false);
			}
		} else {
			console.log(`   ⏸️ Skipping negotiation - no active local tracks to send`);
			this.negotiationInProgress.set(peerId, false);
		}
	} catch (error) {
		console.error('❌ Error handling negotiation needed:', error);
		console.error('   Error details:', error.message);
		// Clear negotiation flag on error
		this.negotiationInProgress.set(peerId, false);
	}
	};

		this.peerConnections.set(peerId, pc);
		return pc;
	}

	async getMediaDevices() {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			return {
				audio: devices.filter(d => d.kind === 'audioinput'),
				video: devices.filter(d => d.kind === 'videoinput'),
				audioOutput: devices.filter(d => d.kind === 'audiooutput')
			};
		} catch (error) {
			console.error('Error listing media devices:', error);
			return { audio: [], video: [], audioOutput: [] };
		}
	}

	async startLocalStream(audioDeviceId = null, videoDeviceId = null, triedDevices = { audio: [], video: [] }) {
		try {
			const constraints = {
				audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
				video: videoDeviceId ? 
					{ deviceId: { exact: videoDeviceId }, width: 1280, height: 720 } : 
					{ width: 1280, height: 720 }
			};
			
		console.log('Requesting media devices with constraints:', constraints);
		this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
		console.log('Got media stream:', this.localStream);
		console.log('Stream tracks:', this.localStream.getTracks().map(t => ({ kind: t.kind, label: t.label, enabled: t.enabled })));
		
		// Mark local stream as ready and process any queued offers
		this.isLocalStreamReady = true;
		console.log(`✅ Local stream ready! Processing ${this.pendingOffers.size} queued offer(s)...`);
		await this.processPendingOffers();
		
		return this.localStream;
		} catch (error) {
			console.error('Error accessing media devices:', error);
			console.error('Error name:', error.name);
			console.error('Error message:', error.message);
			
			// If device is in use and no explicit device was specified, try the next available device
			if (error.name === 'NotReadableError' || error.message.includes('in use')) {
				const devices = await this.getMediaDevices();
				
				// Try next audio device if audio failed and no explicit audio device was specified
				if (!audioDeviceId && devices.audio && devices.audio.length > 0) {
					const availableAudioDevices = devices.audio.filter(d => !triedDevices.audio.includes(d.deviceId));
					if (availableAudioDevices.length > 0) {
						const nextAudioDevice = availableAudioDevices[0];
						console.log(`🔄 Audio device in use, trying next: ${nextAudioDevice.label}`);
						triedDevices.audio.push(nextAudioDevice.deviceId);
						return this.startLocalStream(nextAudioDevice.deviceId, videoDeviceId, triedDevices);
					}
				}
				
				// Try next video device if video failed and no explicit video device was specified
				if (!videoDeviceId && devices.video && devices.video.length > 0) {
					const availableVideoDevices = devices.video.filter(d => !triedDevices.video.includes(d.deviceId));
					if (availableVideoDevices.length > 0) {
						const nextVideoDevice = availableVideoDevices[0];
						console.log(`🔄 Video device in use, trying next: ${nextVideoDevice.label}`);
						triedDevices.video.push(nextVideoDevice.deviceId);
						return this.startLocalStream(audioDeviceId, nextVideoDevice.deviceId, triedDevices);
					}
				}
			}
			
		// If explicit devices were specified and failed, re-throw the original error
		// so the caller can try other device combinations
		if (audioDeviceId || videoDeviceId) {
			// Keep the original error name so fallback logic can detect it
			const deviceError = new Error(error.message);
			deviceError.name = error.name;
			deviceError.originalError = error;
			throw deviceError;
		}
		
		// Provide user-friendly error messages for non-specific device requests
		let userMessage = 'Failed to access camera/microphone';
		if (error.name === 'NotAllowedError') {
			userMessage = 'Camera/microphone access denied. Please allow access in your browser settings.';
		} else if (error.name === 'NotFoundError') {
			userMessage = 'No camera or microphone found on your device.';
		} else if (error.name === 'NotReadableError') {
			userMessage = 'All available devices are in use by another application.';
		}
		
		const enhancedError = new Error(userMessage);
		enhancedError.name = error.name; // Preserve error name
		enhancedError.originalError = error;
		throw enhancedError;
		}
	}

	async switchMediaDevice(kind, deviceId) {
		try {
			const constraints = kind === 'audio' 
				? { audio: { deviceId: { exact: deviceId } }, video: false }
				: { audio: false, video: { deviceId: { exact: deviceId }, width: 1280, height: 720 } };

			const newStream = await navigator.mediaDevices.getUserMedia(constraints);
			const newTrack = newStream.getTracks()[0];

			if (!this.localStream) {
				this.localStream = newStream;
				return newStream;
			}

			// Replace track in local stream
			const oldTrack = this.localStream.getTracks().find(t => t.kind === (kind === 'audio' ? 'audio' : 'video'));
			if (oldTrack) {
				this.localStream.removeTrack(oldTrack);
				oldTrack.stop();
			}
			this.localStream.addTrack(newTrack);

			// Replace track in peer connections
			// If screen sharing is active, don't replace video track (it's currently the screen track)
			// The new camera track will be used when screen sharing stops
			if (kind === 'audio' || !this.isScreenSharing) {
				this.peerConnections.forEach((pc) => {
					const sender = pc.getSenders().find(s => s.track && s.track.kind === (kind === 'audio' ? 'audio' : 'video'));
					if (sender) {
						sender.replaceTrack(newTrack);
					}
				});
			}

			return this.localStream;
		} catch (error) {
			console.error(`Error switching ${kind} device:`, error);
			throw error;
		}
	}

	async connectToPeer(userId) {
		// Check reconnection cooldown (prevent rapid reconnection after failures)
		const cooldownTime = this.reconnectionCooldown.get(userId);
		if (cooldownTime) {
			const timeSinceFailure = Date.now() - cooldownTime;
			const cooldownPeriod = 5000; // 5 seconds
			if (timeSinceFailure < cooldownPeriod) {
				const remainingTime = Math.ceil((cooldownPeriod - timeSinceFailure) / 1000);
				console.log(`⏱️ Reconnection cooldown active for ${userId}. Wait ${remainingTime} more second(s) before reconnecting.`);
				return; // Skip connection attempt during cooldown
			} else {
				// Cooldown expired, clear it
				console.log(`✅ Reconnection cooldown expired for ${userId}, allowing new connection`);
				this.reconnectionCooldown.delete(userId);
			}
		}
		
		console.log(`Initiating connection to user ${userId}`);
		// For outgoing connections, we don't know their sessionId yet, so we use userId as temporary peerId
		const tempPeerId = userId;
		// This will create the connection and add tracks
		// IMPORTANT: Adding tracks will trigger onnegotiationneeded, which will create and send the offer automatically
		// We do NOT need to manually create an offer here, doing so causes race conditions and m-line errors
		await this.createPeerConnection(tempPeerId, userId);
	}

	async processPendingOffers() {
		if (this.pendingOffers.size === 0) {
			return;
		}
		
		console.log(`📥 Processing ${this.pendingOffers.size} pending offer(s)...`);
		const offers = Array.from(this.pendingOffers.entries());
		this.pendingOffers.clear();
		
		for (const [sessionId, offer] of offers) {
			console.log(`   Processing queued offer from ${sessionId}`);
			await this.handleOffer(sessionId, offer);
		}
	}

	async handleOffer(sessionId, offer) {
		console.log(`📨 handleOffer called for session ${sessionId}`);
		
		// If local stream isn't ready yet, queue this offer
		if (!this.isLocalStreamReady) {
			console.log(`⏸️ Local stream not ready yet, queuing offer from ${sessionId}`);
			this.pendingOffers.set(sessionId, offer);
			return;
		}
		
		// Extract userId from sessionId
		const userId = sessionId.split('_')[0];
		
		// Check for existing connection by sessionId first
		let pc = this.peerConnections.get(sessionId);
		
	// If no connection by sessionId, check if we have one by userId (we initiated it)
	if (!pc) {
		pc = this.peerConnections.get(userId);
		if (pc) {
			// We found a connection we initiated - move it to the correct sessionId
			console.log(`Moving peer connection from ${userId} to ${sessionId}`);
			this.peerConnections.delete(userId);
			this.peerConnections.set(sessionId, pc);
			
			// Move negotiation flag as well
			const negotiationFlag = this.negotiationInProgress.get(userId);
			if (negotiationFlag !== undefined) {
				this.negotiationInProgress.set(sessionId, negotiationFlag);
				this.negotiationInProgress.delete(userId);
			}
		}
	}
		
	console.log(`Current connection state:`, pc && pc.signalingState);
	
	// Handle glare situation: both peers sent offers simultaneously
	// Use session IDs as tie-breaker (handles same user with multiple sessions)
	if (pc && pc.signalingState !== 'stable') {
		console.log(`⚡ Glare detected with session ${sessionId}. My session: ${this.sessionId}, Peer session: ${sessionId}`);
		
		// If our session ID is "greater", we keep our offer and ignore theirs
		// If our session ID is "less", we discard our offer and accept theirs (rollback)
		const shouldIgnoreOffer = this.sessionId > sessionId;
		
		if (shouldIgnoreOffer) {
			console.log(`🚫 Ignoring offer from ${sessionId} due to tie-breaker rule (our session ID is higher)`);
			return;
		} else {
			console.log(`✅ Accepting offer from ${sessionId} and rolling back our offer (their session ID is higher)`);
			// Use rollback instead of closing connection
			try {
				await pc.setLocalDescription({ type: 'rollback' });
				console.log(`   🔄 Successfully rolled back local offer`);
			} catch (rollbackError) {
				console.error(`   ❌ Rollback failed, recreating connection:`, rollbackError.message);
				pc.close();
				this.peerConnections.delete(sessionId);
				pc = await this.createPeerConnection(sessionId, userId);
			}
		}
	} else if (!pc) {
			console.log(`🆕 Creating new peer connection for ${sessionId}`);
			pc = await this.createPeerConnection(sessionId, userId);
		}

		try {
			console.log(`Setting remote description (offer) from session ${sessionId}`);
			console.log(`   Current transceivers before setting remote description:`, pc.getTransceivers().length);
			
		// Capture current receiver tracks BEFORE setting remote description
		// This allows us to detect when tracks are removed during renegotiation
		const transceiversBefore = pc.getTransceivers();
		const tracksBeforeByMid = new Map();
		let videoTrackIndexBefore = 0; // Track which video track this is (0 = first/camera, 1+ = screen)
		let liveVideoTracksBefore = 0;
		
		transceiversBefore.forEach(t => {
			// Check ALL transceivers with tracks, not just live ones
			// This is critical for detecting track removals
			if (t.receiver && t.receiver.track && t.mid) {
				const isVideoTrack = t.receiver.track.kind === 'video';
				const isLive = t.receiver.track.readyState === 'live';
				const isActive = t.direction !== 'inactive' && t.currentDirection !== 'inactive';
				const videoIndex = isVideoTrack ? videoTrackIndexBefore++ : -1;
				
				if (isVideoTrack && isLive) {
					liveVideoTracksBefore++;
				}
				
				tracksBeforeByMid.set(t.mid, {
					track: t.receiver.track,
					kind: t.receiver.track.kind,
					id: t.receiver.track.id,
					isLive: isLive,
					isActive: isActive,
					direction: t.direction,
					currentDirection: t.currentDirection,
					isScreenTrack: isVideoTrack && videoIndex >= 1 // Second+ video track is screen
				});
			}
		});
		console.log(`   📊 Receiver tracks before:`, tracksBeforeByMid.size, `(${videoTrackIndexBefore} video tracks, ${liveVideoTracksBefore} live)`);
			
			await pc.setRemoteDescription(new RTCSessionDescription(offer));
			
			console.log(`✅ Remote description set!`);
			console.log(`   Transceivers after setting remote description:`, pc.getTransceivers().length);
			
			// Detect removed or stopped tracks by comparing transceivers before/after
			const transceiversAfter = pc.getTransceivers();
			const tracksAfterByMid = new Map();
			const transceiversByMid = new Map(); // Also track transceiver states
			let videoTrackIndexAfter = 0;
			let liveVideoTracksAfter = 0;
			
			transceiversAfter.forEach(t => {
				if (t.mid) {
					// Store transceiver info regardless of track state
					transceiversByMid.set(t.mid, {
						direction: t.direction,
						currentDirection: t.currentDirection
					});
					
					if (t.receiver && t.receiver.track) {
						const isLive = t.receiver.track.readyState === 'live';
						const isVideoTrack = t.receiver.track.kind === 'video';
						const isActive = t.direction !== 'inactive' && t.currentDirection !== 'inactive';
						
						if (isVideoTrack) {
							videoTrackIndexAfter++;
							if (isLive && isActive) {
								liveVideoTracksAfter++;
							}
						}
						
						tracksAfterByMid.set(t.mid, {
							track: t.receiver.track,
							kind: t.receiver.track.kind,
							id: t.receiver.track.id,
							isLive: isLive,
							isActive: isActive
						});
					}
				}
			});
		console.log(`   📊 Receiver tracks after:`, tracksAfterByMid.size, `(${videoTrackIndexAfter} video tracks, ${liveVideoTracksAfter} live)`);
		
		// Check for removed, stopped, or deactivated tracks
		tracksBeforeByMid.forEach((beforeInfo, mid) => {
			const afterInfo = tracksAfterByMid.get(mid);
			const transceiverInfo = transceiversByMid.get(mid);
			
			// Track was removed, stopped, or transceiver became inactive
			const isRemoved = !afterInfo;
			const wasStopped = beforeInfo.isLive && afterInfo && !afterInfo.isLive; // Was live, now stopped
			const becameInactive = beforeInfo.isActive && afterInfo && !afterInfo.isActive; // Was active, now inactive
			const transceiverBecameInactive = beforeInfo.isActive && transceiverInfo && 
				(transceiverInfo.direction === 'inactive' || transceiverInfo.currentDirection === 'inactive');
				
			if (isRemoved || wasStopped || becameInactive || transceiverBecameInactive) {
				let reason = 'unknown';
				if (isRemoved) reason = 'removed';
				else if (wasStopped) reason = 'stopped';
				else if (becameInactive || transceiverBecameInactive) reason = 'deactivated';
					
				console.log(`   🔴 Track ${reason} during renegotiation: ${beforeInfo.kind} (${beforeInfo.id})`);
				console.log(`   🔍 Track type: ${beforeInfo.isScreenTrack ? '🖥️ SCREEN' : '📹 CAMERA'}`);
				console.log(`   📡 Transceiver BEFORE: direction=${beforeInfo.direction}, currentDirection=${beforeInfo.currentDirection}, active=${beforeInfo.isActive}`);
				if (transceiverInfo) {
					console.log(`   📡 Transceiver AFTER: direction=${transceiverInfo.direction}, currentDirection=${transceiverInfo.currentDirection}`);
				}
					
					if (beforeInfo.kind === 'video') {
						// Decrement video track count
						const count = this.peerVideoTrackCounts.get(sessionId) || 0;
						if (count > 0) {
							this.peerVideoTrackCounts.set(sessionId, count - 1);
							console.log(`   📊 Video track count for peer ${sessionId} decreased to: ${count - 1}`);
						}
						
						// Notify UI if this was a screen track
						if (beforeInfo.isScreenTrack && this.onRemoteStreamCallback) {
							console.log(`   📢 Notifying UI that screen track was removed/stopped`);
							this.onRemoteStreamCallback(sessionId, null, true); // null stream with isScreenTrack = true
						}
					}
				}
			});
			
		console.log(`   Transceiver details:`, pc.getTransceivers().map((t, i) => ({
			index: i,
			direction: t.direction,
			currentDirection: t.currentDirection,
			mid: t.mid,
			sender: t.sender.track ? {
				kind: t.sender.track.kind,
				label: t.sender.track.label,
				id: t.sender.track.id
			} : 'no track',
			receiver: t.receiver.track ? {
				kind: t.receiver.track.kind,
				label: t.receiver.track.label,
				id: t.receiver.track.id,
				readyState: t.receiver.track.readyState
			} : 'no track'
		})));
		
		// Additional check: Clean up any inactive screen transceivers
		// This handles cases where the screen share was stopped in a previous negotiation
		// but we missed detecting it, leaving a frozen screen in the UI
		console.log(`   🔍 Checking for stale inactive screen transceivers...`);
		const allTransceivers = pc.getTransceivers();
		let videoTransceiverIndex = 0;
		let activeVideoTransceivers = 0;
		
		// First pass: count active video transceivers
		allTransceivers.forEach(t => {
			if (t.receiver && t.receiver.track && t.receiver.track.kind === 'video') {
				const isActive = t.currentDirection !== 'inactive' && t.direction !== 'inactive' && t.receiver.track.readyState === 'live';
				if (isActive) {
					activeVideoTransceivers++;
				}
			}
		});
		
		console.log(`      📊 Active video transceivers in offer: ${activeVideoTransceivers}`);
		console.log(`      📊 Current video track count for peer: ${this.peerVideoTrackCounts.get(sessionId) || 0}`);
		
	// Second pass: check for inactive or reduced count
	// Note: This check is less reliable than the count synchronization below,
	// so we only use it for logging. The count sync will handle the actual notification.
	allTransceivers.forEach((t, idx) => {
		if (t.receiver && t.receiver.track && t.receiver.track.kind === 'video') {
			const isActive = t.currentDirection !== 'inactive' && t.direction !== 'inactive';
			const isScreenTransceiver = videoTransceiverIndex >= 1; // Second+ video transceiver is screen
			videoTransceiverIndex++;
			
			if (!isActive && isScreenTransceiver) {
				console.log(`   🧹 Found inactive screen transceiver at index ${idx}, mid=${t.mid}`);
				console.log(`      Direction: ${t.direction}, CurrentDirection: ${t.currentDirection}`);
				console.log(`      (Will be handled by track count synchronization below)`);
			}
		}
	});
		
	// Synchronize track count with actual active transceivers
	// This is critical because ontrack events only fire for NEW tracks, not during renegotiation
	const storedCount = this.peerVideoTrackCounts.get(sessionId) || 0;
	
	if (activeVideoTransceivers !== storedCount) {
		console.log(`   🔄 Synchronizing video track count: stored=${storedCount}, actual=${activeVideoTransceivers}`);
		
		// Update the stored count first
		this.peerVideoTrackCounts.set(sessionId, activeVideoTransceivers);
		console.log(`   📊 Updated video track count to: ${activeVideoTransceivers}`);
		
		// If count decreased and we went from multiple tracks to just camera, notify UI
		// This happens when screen share ends: 2 tracks -> 1 track
		if (storedCount > activeVideoTransceivers && storedCount > 1 && activeVideoTransceivers >= 1) {
			console.log(`   🧹 Screen share was removed (${storedCount} -> ${activeVideoTransceivers} tracks)`);
			
			// Notify UI to clear the screen stream and show camera
			if (this.onRemoteStreamCallback) {
				console.log(`      📢 Notifying UI to clear screen stream and show camera`);
				this.onRemoteStreamCallback(sessionId, null, true);
			}
		}
		// If count is being initialized from 0, don't send notifications
		// The ontrack events will handle proper stream attachment
		else if (storedCount === 0) {
			console.log(`   ℹ️ Initializing track count (no notification needed)`);
		}
	}
			
		console.log(`Creating answer...`);
		const answer = await pc.createAnswer();
		console.log(`Setting local description (answer) for session ${sessionId}`);
		await pc.setLocalDescription(answer);
		console.log(`Sending answer to user ${userId}`);
		// Send to userId (not sessionId) so all their sessions receive it
		await this.sendSignal(userId, 'answer', { type: answer.type, sdp: answer.sdp });
		
		// Process any queued ICE candidates now that remote description is set
		await this.processPendingIceCandidates(sessionId);
	} catch (error) {
		console.error('❌ Error handling offer:', error);
	}
	}

	async handleAnswer(sessionId, answer) {
		// Try to find peer connection by sessionId first
		let pc = this.peerConnections.get(sessionId);
		
	// If not found by sessionId, try by userId (we initiated the connection)
	if (!pc) {
		const userId = sessionId.split('_')[0];
		pc = this.peerConnections.get(userId);
		if (pc) {
			// Move connection to correct sessionId
			console.log(`Moving peer connection from ${userId} to ${sessionId}`);
			this.peerConnections.delete(userId);
			this.peerConnections.set(sessionId, pc);
			
			// Move negotiation flag as well
			const negotiationFlag = this.negotiationInProgress.get(userId);
			if (negotiationFlag !== undefined) {
				this.negotiationInProgress.set(sessionId, negotiationFlag);
				this.negotiationInProgress.delete(userId);
			}
		}
	}
	
	if (pc) {
		try {
			console.log(`Received answer from session ${sessionId}, current signaling state: ${pc.signalingState}`);
				
			// Only set remote description if we're in have-local-offer state
			// If we're already stable, this answer is from a stale/superseded offer
			if (pc.signalingState === 'have-local-offer') {
				console.log(`Setting remote description (answer) from session ${sessionId}`);
				await pc.setRemoteDescription(new RTCSessionDescription(answer));
				console.log(`✅ Successfully set remote description for session ${sessionId}`);
				
				// Process any queued ICE candidates now that remote description is set
				await this.processPendingIceCandidates(sessionId);
				
				// Clear negotiation flag now that we've completed the offer/answer exchange
				this.negotiationInProgress.set(sessionId, false);
			} else if (pc.signalingState === 'stable') {
					console.log(`⚠️ Ignoring answer from ${sessionId} - already in stable state (likely from superseded offer)`);
					// Clear negotiation flag in case it was stuck
					this.negotiationInProgress.set(sessionId, false);
				} else {
					console.warn(`⚠️ Unexpected signaling state ${pc.signalingState} when receiving answer from ${sessionId}`);
				}
			} catch (error) {
				console.error('❌ Error handling answer:', error);
				console.error('   Signaling state:', pc.signalingState);
				// Clear negotiation flag on error
				this.negotiationInProgress.set(sessionId, false);
			}
		} else {
			console.warn(`Received answer from session ${sessionId} but no peer connection exists`);
		}
	}

	async handleIceCandidate(sessionId, candidate) {
		// Try to find peer connection by sessionId first
		let pc = this.peerConnections.get(sessionId);
		
	// If not found by sessionId, try by userId (we initiated the connection)
	if (!pc) {
		const userId = sessionId.split('_')[0];
		pc = this.peerConnections.get(userId);
		if (pc) {
			// Move connection to correct sessionId
			console.log(`Moving peer connection from ${userId} to ${sessionId} for ICE candidate`);
			this.peerConnections.delete(userId);
			this.peerConnections.set(sessionId, pc);
			
			// Move negotiation flag as well
			const negotiationFlag = this.negotiationInProgress.get(userId);
			if (negotiationFlag !== undefined) {
				this.negotiationInProgress.set(sessionId, negotiationFlag);
				this.negotiationInProgress.delete(userId);
			}
		}
	}
		
		if (pc) {
			// Check if remote description is set
			if (!pc.remoteDescription) {
				console.log(`⏸️ Queueing ICE candidate from ${sessionId} (remote description not yet set)`);
				if (!this.pendingIceCandidates.has(sessionId)) {
					this.pendingIceCandidates.set(sessionId, []);
				}
				this.pendingIceCandidates.get(sessionId).push(candidate);
				return;
			}
			
			try {
				console.log(`Adding ICE candidate from session ${sessionId}`);
				await pc.addIceCandidate(new RTCIceCandidate(candidate));
			} catch (error) {
				console.error('Error adding ICE candidate:', error);
			}
		} else {
			console.warn(`Received ICE candidate from session ${sessionId} but no peer connection exists`);
		}
	}
	
	async processPendingIceCandidates(sessionId) {
		const pendingCandidates = this.pendingIceCandidates.get(sessionId);
		if (pendingCandidates && pendingCandidates.length > 0) {
			console.log(`📥 Processing ${pendingCandidates.length} queued ICE candidate(s) for ${sessionId}`);
			const pc = this.peerConnections.get(sessionId);
			if (pc && pc.remoteDescription) {
				for (const candidate of pendingCandidates) {
					try {
						await pc.addIceCandidate(new RTCIceCandidate(candidate));
					} catch (error) {
						console.error('Error adding queued ICE candidate:', error);
					}
				}
			}
			this.pendingIceCandidates.delete(sessionId);
		}
	}

	handlePeerLeave(sessionId) {
		console.log(`🚪 handlePeerLeave called for sessionId: ${sessionId}`);
		
		const pc = this.peerConnections.get(sessionId);
		if (pc) {
			console.log(`   Closing peer connection for ${sessionId}`);
			// Completely close the connection and remove all event listeners
			pc.onicecandidate = null;
			pc.ontrack = null;
			pc.onconnectionstatechange = null;
			pc.oniceconnectionstatechange = null;
			pc.onicegatheringstatechange = null;
			pc.onsignalingstatechange = null;
			pc.onnegotiationneeded = null;
			pc.close();
			this.peerConnections.delete(sessionId);
		}
		
		// Also check if we have it by userId
		const userId = sessionId.split('_')[0];
		const pcByUserId = this.peerConnections.get(userId);
		if (pcByUserId) {
			console.log(`   Closing peer connection for userId ${userId}`);
			pcByUserId.onicecandidate = null;
			pcByUserId.ontrack = null;
			pcByUserId.onconnectionstatechange = null;
			pcByUserId.oniceconnectionstatechange = null;
			pcByUserId.onicegatheringstatechange = null;
			pcByUserId.onsignalingstatechange = null;
			pcByUserId.onnegotiationneeded = null;
			pcByUserId.close();
			this.peerConnections.delete(userId);
		}
		
	// Clean up video track count and tracked tracks
	this.peerVideoTrackCounts.delete(sessionId);
	this.peerVideoTrackCounts.delete(userId);
	this.peerTrackedTracks.delete(sessionId);
	this.peerTrackedTracks.delete(userId);
	
	// Clean up negotiation state
	this.negotiationInProgress.delete(sessionId);
	this.negotiationInProgress.delete(userId);
		
	// Clean up any pending offers and ICE candidates from this peer
	this.pendingOffers.delete(sessionId);
	this.pendingOffers.delete(userId);
	this.pendingIceCandidates.delete(sessionId);
	this.pendingIceCandidates.delete(userId);
		
		console.log(`   ✅ Cleanup complete for ${sessionId}`);
		
		if (this.onRemoteStreamRemovedCallback) {
			this.onRemoteStreamRemovedCallback(sessionId);
		}
	}

	async startScreenShare() {
		try {
			// Check if getDisplayMedia is supported
			if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
				const error = new Error('Screen sharing is not supported on this device/browser. Screen sharing is not available on iOS Safari.');
				error.name = 'NotSupportedError';
				throw error;
			}

			const screenConstraints = {
				video: {
					displaySurface: 'window'
				},
				audio: false
			};
			
			this.screenStream = await navigator.mediaDevices.getDisplayMedia(screenConstraints);
			
			// ADD screen track to peer connections (don't replace camera)
			const screenTrack = this.screenStream.getVideoTracks()[0];
			
			console.log('🖥️ Adding screen track to peer connections (keeping camera active)');
			this.peerConnections.forEach((pc, peerId) => {
				try {
					// Add screen track as a second video track
					const sender = pc.addTrack(screenTrack, this.screenStream);
					console.log(`✅ Added screen track to peer ${peerId}`);
					console.log(`   Total senders now: ${pc.getSenders().length}`);
					console.log(`   Sender track: ${sender.track ? sender.track.label : 'none'}`);
				} catch (error) {
					console.error(`❌ Error adding screen track to peer ${peerId}:`, error);
				}
			});
			
			console.log(`🔄 Screen track added to ${this.peerConnections.size} peer connection(s)`);
			console.log(`   This should trigger onnegotiationneeded on each peer connection`);
			
			// Handle screen share stop (when user clicks browser's "Stop Sharing" button)
			screenTrack.onended = () => {
				console.log('🛑 Screen share ended by browser (user clicked Stop Sharing button)');
				this.stopScreenShare();
			};
			
			this.isScreenSharing = true;
			return this.screenStream;
		} catch (error) {
			console.error('Error starting screen share:', error);
			throw error;
		}
	}

	async stopScreenShare() {
		if (this.screenStream) {
			const screenTrack = this.screenStream.getVideoTracks()[0];
			
			// Verify we have a local camera stream before proceeding
			if (!this.localStream || !this.localStream.active) {
				console.error('❌ Local camera stream is not active! Cannot properly renegotiate.');
				console.log('   📊 localStream:', this.localStream);
			} else {
				console.log('✅ Local camera stream is active:', {
					streamId: this.localStream.id,
					tracks: this.localStream.getTracks().map(t => ({
						kind: t.kind,
						id: t.id,
						readyState: t.readyState,
						enabled: t.enabled
					}))
				});
			}
			
			// Remove screen track from peer connections (keep camera)
			console.log('🖥️ Removing screen track from peer connections (keeping camera)');
			
			// Process each peer connection
			const peerEntries = Array.from(this.peerConnections.entries());
			for (const [peerId, pc] of peerEntries) {
				const screenSender = pc.getSenders().find(s => s.track === screenTrack);
				if (screenSender) {
					// Temporarily disable onnegotiationneeded to prevent automatic renegotiation
					// We want to manually control when the offer is sent
					const originalNegotiationHandler = pc.onnegotiationneeded;
					pc.onnegotiationneeded = null;
					console.log(`   🔇 Disabled automatic onnegotiationneeded for peer ${peerId}`);
					
					pc.removeTrack(screenSender);
					console.log(`Removed screen track from peer ${peerId}`);
					
					// Wait a brief moment for track removal to be processed
					await new Promise(resolve => setTimeout(resolve, 100));
					
					// Verify we still have camera tracks before renegotiating
					const allSenders = pc.getSenders();
					const remainingSenders = allSenders.filter(s => s.track && s.track.readyState === 'live');
					console.log(`   📊 All senders: ${allSenders.length}, Live senders: ${remainingSenders.length}`);
					console.log(`   📊 Remaining live senders after removing screen:`, remainingSenders.map(s => ({
						kind: s.track ? s.track.kind : 'no-track',
						id: s.track ? s.track.id : 'n/a',
						readyState: s.track ? s.track.readyState : 'n/a'
					})));
					
				// Manually trigger renegotiation now that the track is removed
				if (pc.signalingState === 'stable' && !this.negotiationInProgress.get(peerId)) {
					// Only renegotiate if we have remaining tracks (camera)
					if (remainingSenders.length >= 2) { // Should have audio + video (camera)
						console.log(`📡 Manually creating offer after removing screen track for peer ${peerId}`);
						
						// Mark negotiation as in progress
						this.negotiationInProgress.set(peerId, true);
						
						try {
							const offer = await pc.createOffer();
							console.log(`   📋 Offer created, checking transceivers...`);
							const transceivers = pc.getTransceivers();
							console.log(`   📊 Transceivers in offer:`, transceivers.map((t, i) => ({
								index: i,
								direction: t.direction,
								mid: t.mid,
								kind: t.sender.track ? t.sender.track.kind : 'no-track'
							})));
							
							await pc.setLocalDescription(offer);
							
							// Send to userId (extract from peerId which might be a sessionId)
							const userId = peerId.split('_')[0];
							await this.sendSignal(userId, 'offer', { type: offer.type, sdp: offer.sdp });
							console.log(`✅ Sent updated offer without screen track to ${userId}`);
						} catch (error) {
							console.error(`❌ Error renegotiating after removing screen track:`, error);
						} finally {
							// Clear negotiation flag after a delay
							setTimeout(() => {
								this.negotiationInProgress.set(peerId, false);
							}, 500);
						}
					} else {
						console.warn(`⚠️ Insufficient tracks after removing screen (expected 2+, got ${remainingSenders.length}) - not sending offer to ${peerId}`);
					}
				} else {
					if (pc.signalingState !== 'stable') {
						console.log(`⏸️ Cannot renegotiate for peer ${peerId} - signaling state is ${pc.signalingState}`);
					} else if (this.negotiationInProgress.get(peerId)) {
						console.log(`⏸️ Cannot renegotiate for peer ${peerId} - negotiation already in progress`);
					}
				}
					
					// Re-enable onnegotiationneeded handler
					pc.onnegotiationneeded = originalNegotiationHandler;
					console.log(`   🔊 Re-enabled automatic onnegotiationneeded for peer ${peerId}`);
				}
			}
			
			// Stop the screen stream
			this.screenStream.getTracks().forEach(track => track.stop());
			this.screenStream = null;
		}
		
		this.isScreenSharing = false;
		
		// Notify the UI component that screen sharing has stopped
		if (this.onScreenShareStoppedCallback) {
			console.log('📢 Notifying UI that screen share stopped');
			this.onScreenShareStoppedCallback();
		}
	}

	toggleAudio(enabled) {
		if (this.localStream) {
			const audioTracks = this.localStream.getAudioTracks();
			audioTracks.forEach(track => {
				track.enabled = enabled;
			});
		}
	}

	toggleVideo(enabled) {
		if (this.localStream) {
			const videoTracks = this.localStream.getVideoTracks();
			videoTracks.forEach(track => {
				track.enabled = enabled;
			});
		}
	}

	async cleanup() {
		//console.log('🧹 Cleaning up WebRTC service...');
		
		// Stop presence heartbeat FIRST to prevent race conditions
		this.stopPresenceHeartbeat();
		
		// Store IDs before clearing them (in case intervals were already queued)
		const finalVaultId = this.vaultId;
		const finalSessionId = this.sessionId;
		const finalUserId = this.userId;
		
		// Clear IDs immediately to prevent any queued callbacks from using them
		this.vaultId = null;
		this.sessionId = null;
		this.userId = null;
		
		// Send final offline presence update with stored IDs
		if (finalVaultId && finalSessionId) {
			try {
				await this.updatePresenceWithIds(finalVaultId, finalSessionId, 'offline');
			} catch (error) {
				console.error('Error sending final presence update:', error);
			}
		}

		// Unsubscribe from signals
		if (this.signalSubscription) {
			this.signalSubscription.unsubscribe();
			this.signalSubscription = null;
		}

		// Notify peers we're leaving
		const peerIds = Array.from(this.peerConnections.keys());
		if (peerIds.length > 0) {
			console.log(`📤 Notifying ${peerIds.length} peer(s) we're leaving...`);
		}
		// Get unique user IDs from peer connections (some might be userId, some sessionId)
		const uniqueUserIds = new Set();
		for (const peerId of peerIds) {
			const userId = peerId.includes('_') ? peerId.split('_')[0] : peerId;
			uniqueUserIds.add(userId);
		}
		
		// Only send leave signals if we still have valid IDs
		if (finalVaultId && finalUserId) {
			for (const userId of uniqueUserIds) {
				try {
					await this.sendSignalWithIds(finalVaultId, finalSessionId, finalUserId, userId, 'leave', {});
				} catch (error) {
					console.error(`Error sending leave signal to ${userId}:`, error);
				}
			}
		}

	// Close all peer connections
	if (this.peerConnections.size > 0) {
		console.log(`🔌 Closing ${this.peerConnections.size} peer connection(s)...`);
		this.peerConnections.forEach((pc) => {
			pc.close();
		});
		this.peerConnections.clear();
	}
	
	// Clear negotiation state and reconnection cooldowns
	this.negotiationInProgress.clear();
	this.reconnectionCooldown.clear();

		// Stop local streams
		if (this.localStream) {
			console.log('🎥 Stopping local media stream...');
			this.localStream.getTracks().forEach(track => track.stop());
			this.localStream = null;
		}

		if (this.screenStream) {
			console.log('🖥️ Stopping screen share stream...');
			this.screenStream.getTracks().forEach(track => track.stop());
			this.screenStream = null;
		}

		//console.log('✅ WebRTC cleanup complete');
	}

	// Get the current session ID
	getSessionId() {
		return this.sessionId;
	}

	// Diagnostic method to check connection status
	getConnectionStats() {
		const stats = {
			sessionId: this.sessionId,
			userId: this.userId,
			totalPeers: this.peerConnections.size,
			peers: []
		};

		this.peerConnections.forEach((pc, peerId) => {
			stats.peers.push({
				peerId: peerId,
				connectionState: pc.connectionState,
				iceConnectionState: pc.iceConnectionState,
				iceGatheringState: pc.iceGatheringState,
				signalingState: pc.signalingState
			});
		});

		return stats;
	}
}

export default new WebRTCService();


